import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPen,
  faPlus,
  faTrash,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import { User } from "../../../../../core/models/entities/User";
import { CreateUserDto } from "../../../../../core/models/dtos/CreateUserDto";
import { UpdateUserDto } from "../../../../../core/models/dtos/UpdateUserDto";
import { PrimaryButton } from "../../../../components/Buttons/PrimaryButton";
import { SecondaryButton } from "../../../../components/Buttons/SecondaryButton";
import { BadgeChip } from "../../../../components/OrgShell/BadgeChip";
import { FieldGroup } from "../../../../components/OrgShell/FieldGroup";
import {
  DialogFrame,
  useDialog,
} from "../../../../components/Dialogs/dialog-base";
import { CreateUserDialog } from "../../../../components/Organization/user-list.component";
import { InputWrapper } from "../../../../components/Input/InputWrapper";
import { InputLabel } from "../../../../components/Input/InputLabel";
import { CustomInput } from "../../../../components/Input/CustomInput";
import { CustomPhoneInput } from "../../../../components/Input/CustomPhoneInput";
import { BoxHeaderTypography } from "../../../../components/Typographies/BoxHeaderTypography";
import LoadingElementIsolated from "../../../../components/loading-element/loading-element-isolated";

export interface ContactFields {
  contactPerson: string;
  contactPhone: string;
  contactEmail: string;
}

interface UsersContactSectionProps {
  users?: User[];
  isLoading: boolean;
  contact: ContactFields;
  /** Writes the org contact fields; persisted via Save changes (updateOrganization body). */
  onContactChange: (contact: ContactFields) => void;
  onCreateUser: (user: CreateUserDto) => Promise<void>;
  onDeleteUser: (userId: number) => Promise<void>;
  onUpdateUser: (user: User, dto: UpdateUserDto) => Promise<void>;
  onToggleUserHidePrices: (user: User, hidePrices: boolean) => Promise<void>;
  /** Vendor orgs: contact is read-only and per-user price hiding is not available. */
  isVendor?: boolean;
  /** Explain line rendered under the user table (e.g. the workshop hide-prices note). */
  hidePricesNote?: string;
}

export function UsersContactSection({
  users,
  isLoading,
  contact,
  onContactChange,
  onCreateUser,
  onDeleteUser,
  onUpdateUser,
  onToggleUserHidePrices,
  isVendor,
  hidePricesNote,
}: UsersContactSectionProps) {
  const createUserDialog = useDialog();
  const changeContactDialog = useDialog();
  const [userBeingEdited, setUserBeingEdited] = useState<User | undefined>();
  const editUserDialog = useDialog();

  function handleCreateUser(user: CreateUserDto) {
    onCreateUser(user).then(createUserDialog.closeDialog);
  }

  async function handleDeleteUser(user: User) {
    if (
      window.confirm(`Are you sure you want to delete the user ${user.email}`)
    ) {
      await onDeleteUser(user.id);
    }
  }

  function openEditUser(user: User) {
    setUserBeingEdited(user);
    editUserDialog.openDialog();
  }

  if (isLoading || !users) {
    return (
      <div className="relative h-24">
        <LoadingElementIsolated transparent />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <CreateUserDialog
        dialogOptions={createUserDialog}
        onCreate={handleCreateUser}
      />
      <ChangeContactDialog
        dialogOptions={changeContactDialog}
        users={users}
        contact={contact}
        onApply={(next) => {
          onContactChange(next);
          changeContactDialog.closeDialog();
        }}
      />
      {userBeingEdited && (
        <EditUserDialog
          key={userBeingEdited.id}
          dialogOptions={editUserDialog}
          user={userBeingEdited}
          onApply={(dto) => {
            onUpdateUser(userBeingEdited, dto).then(
              editUserDialog.closeDialog
            );
          }}
        />
      )}

      <FieldGroup
        title="Primary contact"
        description="Shown on orders and used for communication. Applied when you save changes."
      >
        <div className="flex items-center gap-4 rounded-md border bg-gray-150 p-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-10 text-primary-100">
            <FontAwesomeIcon icon={faUser} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-gray-700">
              {contact.contactPerson || "No contact person"}
            </p>
            <p className="truncate text-xs text-gray-500">
              {[contact.contactEmail, contact.contactPhone]
                .filter(Boolean)
                .join(" · ") || "No contact details"}
            </p>
          </div>
          {!isVendor && (
            <SecondaryButton onClick={changeContactDialog.openDialog}>
              Change
            </SecondaryButton>
          )}
        </div>
      </FieldGroup>

      <FieldGroup title={`Users (${users.length})`}>
        <div className="max-h-96 overflow-y-auto rounded border">
          <table className="w-full table-auto">
            <thead className="sticky top-0 bg-[#FCFDFE] text-left">
              <tr>
                <th className="py-3 pl-4 text-xs font-medium text-gray-600">
                  Name
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-600">
                  Email
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-600">
                  Phone
                </th>
                {!isVendor && (
                  <th className="px-4 py-3 text-xs font-medium text-gray-600">
                    Hide prices
                  </th>
                )}
                <th className="w-20 px-4 py-3 text-xs font-medium text-gray-600">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className="border-b hover:bg-gray-50">
                  <td className="py-2 pl-4 text-xs">
                    <span className="flex flex-wrap items-center gap-2">
                      {`${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() ||
                        "—"}
                      {user.hidePrices && (
                        <BadgeChip tone="orange">Prices hidden</BadgeChip>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs">{user.email}</td>
                  <td className="px-4 py-2 text-xs">
                    {user.phone ? formatPhoneNumberIntl(user.phone) : ""}
                  </td>
                  {!isVendor && (
                    <td className="px-4 py-2 text-xs">
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={user.hidePrices ?? false}
                        onChange={(e) =>
                          onToggleUserHidePrices(user, e.target.checked)
                        }
                      />
                    </td>
                  )}
                  <td className="px-4 py-2 text-xs">
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        title="Edit user"
                        className="text-gray-500 hover:text-gray-700"
                        onClick={() => openEditUser(user)}
                      >
                        <FontAwesomeIcon icon={faPen} />
                      </button>
                      <button
                        type="button"
                        title="Delete user"
                        className="text-gray-500 hover:text-red"
                        onClick={() => handleDeleteUser(user)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td
                    colSpan={isVendor ? 4 : 5}
                    className="px-4 py-6 text-center text-xs text-gray-500"
                  >
                    No users yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {hidePricesNote && (
          <p className="text-xs text-gray-500">{hidePricesNote}</p>
        )}
        <div>
          <PrimaryButton
            width="md"
            className="px-4"
            onClick={createUserDialog.openDialog}
          >
            <FontAwesomeIcon icon={faPlus} />
            <span className="flex-1">Add user</span>
          </PrimaryButton>
        </div>
      </FieldGroup>
    </div>
  );
}

// ----- Change primary contact -----

function ChangeContactDialog({
  dialogOptions,
  users,
  contact,
  onApply,
}: {
  dialogOptions: ReturnType<typeof useDialog>;
  users: User[];
  contact: ContactFields;
  onApply: (contact: ContactFields) => void;
}) {
  const [selectedUserId, setSelectedUserId] = useState<number | "custom">(
    "custom"
  );
  const [custom, setCustom] = useState<ContactFields>(contact);

  // Keep the custom fields in sync when the dialog opens with new data.
  const [lastOpen, setLastOpen] = useState(false);
  if (dialogOptions.isOpen !== lastOpen) {
    setLastOpen(dialogOptions.isOpen);
    if (dialogOptions.isOpen) {
      setCustom(contact);
      const match = users.find(
        (u) =>
          u.email === contact.contactEmail &&
          `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() ===
            contact.contactPerson
      );
      setSelectedUserId(match ? match.id : "custom");
    }
  }

  function handleApply() {
    if (selectedUserId === "custom") {
      onApply(custom);
      return;
    }
    const user = users.find((u) => u.id === selectedUserId);
    if (!user) return;
    onApply({
      contactPerson: `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim(),
      contactPhone: user.phone ?? "",
      contactEmail: user.email ?? "",
    });
  }

  return (
    <DialogFrame dialogOption={dialogOptions}>
      <div className="space-y-6">
        <BoxHeaderTypography>Change primary contact</BoxHeaderTypography>
        <div className="max-h-60 space-y-1 overflow-y-auto">
          {users.map((user) => (
            <label
              key={user.id}
              className="flex cursor-pointer items-center gap-2 rounded border p-2 text-xs hover:bg-gray-50"
            >
              <input
                type="radio"
                name="primary-contact"
                checked={selectedUserId === user.id}
                onChange={() => setSelectedUserId(user.id)}
              />
              <span className="flex-1">
                <span className="block font-medium">
                  {`${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() ||
                    user.email}
                </span>
                <span className="block text-gray-500">
                  {[user.email, user.phone].filter(Boolean).join(" · ")}
                </span>
              </span>
            </label>
          ))}
          <label className="flex cursor-pointer items-center gap-2 rounded border p-2 text-xs hover:bg-gray-50">
            <input
              type="radio"
              name="primary-contact"
              checked={selectedUserId === "custom"}
              onChange={() => setSelectedUserId("custom")}
            />
            <span className="font-medium">Custom contact</span>
          </label>
        </div>
        {selectedUserId === "custom" && (
          <div className="space-y-2">
            <InputWrapper>
              <InputLabel>Contact person</InputLabel>
              <CustomInput
                name="contactPerson"
                value={custom.contactPerson}
                onChange={(e) =>
                  setCustom({ ...custom, contactPerson: e.target.value })
                }
              />
            </InputWrapper>
            <InputWrapper>
              <InputLabel>Contact email</InputLabel>
              <CustomInput
                type="email"
                name="contactEmail"
                value={custom.contactEmail}
                onChange={(e) =>
                  setCustom({ ...custom, contactEmail: e.target.value })
                }
              />
            </InputWrapper>
            <InputWrapper>
              <InputLabel>Contact phone</InputLabel>
              <CustomPhoneInput
                name="contactPhone"
                value={custom.contactPhone}
                disabled={false}
                onChange={(e) =>
                  setCustom({ ...custom, contactPhone: e.target.value })
                }
              />
            </InputWrapper>
          </div>
        )}
        <p className="text-xs text-gray-500">
          The primary contact is applied when you press Save changes.
        </p>
        <div className="flex justify-center">
          <PrimaryButton width="md" onClick={handleApply}>
            Apply
          </PrimaryButton>
        </div>
      </div>
    </DialogFrame>
  );
}

// ----- Edit user -----

function EditUserDialog({
  dialogOptions,
  user,
  onApply,
}: {
  dialogOptions: ReturnType<typeof useDialog>;
  user: User;
  onApply: (dto: UpdateUserDto) => void;
}) {
  const [firstName, setFirstName] = useState(user.firstName ?? "");
  const [lastName, setLastName] = useState(user.lastName ?? "");
  const [phone, setPhone] = useState(user.phone ?? "");

  return (
    <DialogFrame dialogOption={dialogOptions}>
      <div className="space-y-6">
        <BoxHeaderTypography>Edit user</BoxHeaderTypography>
        <div className="space-y-4">
          <InputWrapper>
            <InputLabel>First name</InputLabel>
            <CustomInput
              name="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </InputWrapper>
          <InputWrapper>
            <InputLabel>Last name</InputLabel>
            <CustomInput
              name="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </InputWrapper>
          <InputWrapper>
            <InputLabel>Phone</InputLabel>
            <CustomPhoneInput
              name="phone"
              value={phone}
              disabled={false}
              onChange={(e) => setPhone(e.target.value)}
            />
          </InputWrapper>
          <p className="text-xs text-gray-500">
            Email: {user.email} (cannot be changed)
          </p>
          <div className="flex justify-center">
            <PrimaryButton
              width="md"
              onClick={() => onApply({ firstName, lastName, phone })}
            >
              Save user
            </PrimaryButton>
          </div>
        </div>
      </div>
    </DialogFrame>
  );
}
